import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleOutline } from '@iwoca/icons';
import {
    PutStateRequestBody,
    fetchGetStateByStateKey,
    fetchPutState
} from '@iwoca/lapi-client/edge';
import { Button, CheckBox } from '@iwoca/orion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import AddressFields from './fields/AddressFields';
import DOBFields from './fields/DOBFields';
import NameFields from './fields/NameFields';
import {
    convertDeserialisedPersonToFormValues,
    createCustomerStateWithNewOrUpdatedPerson
} from './MSHForm.helpers';
import messages from './MSHForm.messages';
import { TFormValues, TPerson } from './MSHForm.types';
import styles from './MSHModal.module.css';
import { useIsInSelfServeSplitTest } from '../../../../../customHooks/useIsInSelfServeSplitTest';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';

export const MSHForm = ({
    onCancel,
    onSubmitSuccess,
    onSubmitError,
    personBeingEdited
}: {
    onCancel: () => void;
    onSubmitSuccess: () => void;
    onSubmitError: () => void;
    personBeingEdited: TPerson | undefined;
}) => {
    const defaultFormValues = personBeingEdited
        ? convertDeserialisedPersonToFormValues(personBeingEdited)
        : {
              address: { postcode: '' }
          };

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        getValues,
        setValue
    } = useForm<TFormValues>({
        defaultValues: defaultFormValues
    });

    const stateKey = useStateKey();
    const isInSelfServeSplitTest = useIsInSelfServeSplitTest();

    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey })
    });

    const queryClient = useQueryClient();

    const saveCustomerState = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        }
    });

    const onSubmit: SubmitHandler<TFormValues> = async (values) => {
        if (!customerState) return;

        // Create a payload with new person
        const newState = createCustomerStateWithNewOrUpdatedPerson(
            values,
            customerState
        );

        try {
            await saveCustomerState.mutateAsync(newState);
            tracking.ampli.drawdownMshPersonAdded();
            onSubmitSuccess();
        } catch (e) {
            console.error(e);
            onSubmitError();
        }
    };

    const isApplicantBeingEdited = personBeingEdited?.isApplicant ?? false;

    return (
        <div className={styles.formWrap}>
            <h1>
                <FormattedMessage {...messages.title} />
            </h1>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                <div>
                    {/* Name fields */}
                    <NameFields
                        errors={errors}
                        register={register}
                        disabled={Boolean(personBeingEdited)}
                    />

                    {/* DOB fields */}
                    <DOBFields
                        errors={errors}
                        register={register}
                        disabled={Boolean(personBeingEdited)}
                    />

                    {/* Roles checkboxes [Director/Shareholder/Guarantor] */}
                    <div className={`${styles.fields} ${styles.roleFields}`}>
                        <label className={styles.label}>
                            This person is a:
                        </label>
                        <CheckBox
                            {...register('director')}
                            label="Director"
                            disabled={isApplicantBeingEdited}
                        />
                        <CheckBox
                            {...register('shareholder')}
                            label="Major Shareholder"
                        />
                        {(personBeingEdited?.isGuarantor &&
                            !isInSelfServeSplitTest) || (
                            <p className={styles.guarantorInfo}>
                                <IconInfoCircleOutline
                                    className={styles.guarantorInfoIcon}
                                    height="20"
                                    width="20"
                                />
                                <FormattedMessage {...messages.guarantorHint} />
                            </p>
                        )}
                    </div>

                    {/* Address fields */}
                    <AddressFields
                        errors={errors}
                        register={register}
                        getValues={getValues}
                        setValue={setValue}
                        disabled={isApplicantBeingEdited}
                    />
                </div>

                {/* Save/Cancel buttons */}
                <div className={styles.formButtonsContainer}>
                    <Button
                        variant="secondary-alt"
                        className={styles.ctaButton}
                        onClick={onCancel}
                    >
                        <FormattedMessage {...messages.cancelCTA} />
                    </Button>

                    <Button
                        variant="primary"
                        className={styles.ctaButton}
                        type="submit"
                        loading={isSubmitting}
                    >
                        <FormattedMessage {...messages.saveCTA} />
                    </Button>
                </div>
            </form>
        </div>
    );
};
